import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { storageFor } from 'ember-local-storage';

/**
 * Watchlist Service
 *
 * Manages a per-event watchlist of entries that the user wants to track.
 * Watchlist is stored in localStorage and persists across sessions.
 */
export default class WatchlistService extends Service {
  @storageFor('watchlist') storage;

  // Tracked version to force reactivity when watchlist changes
  @tracked _version = 0;

  /**
   * Add an entry to the watchlist for an event
   * @param {string} eventUuid - The event UUID
   * @param {string} entryUuid - The entry UUID to add
   */
  addToWatchlist(eventUuid, entryUuid) {
    const watchedByEvent = this.storage.get('watchedEntriesByEvent') || {};
    const entries = watchedByEvent[eventUuid] || [];

    if (!entries.includes(entryUuid)) {
      watchedByEvent[eventUuid] = [...entries, entryUuid];
      this.storage.set('watchedEntriesByEvent', { ...watchedByEvent });
      this._version++;
    }
  }

  /**
   * Remove an entry from the watchlist for an event
   * @param {string} eventUuid - The event UUID
   * @param {string} entryUuid - The entry UUID to remove
   */
  removeFromWatchlist(eventUuid, entryUuid) {
    const watchedByEvent = this.storage.get('watchedEntriesByEvent') || {};
    const entries = watchedByEvent[eventUuid] || [];

    watchedByEvent[eventUuid] = entries.filter(uuid => uuid !== entryUuid);
    this.storage.set('watchedEntriesByEvent', { ...watchedByEvent });
    this._version++;
  }

  /**
   * Check if an entry is on the watchlist for an event
   * @param {string} eventUuid - The event UUID
   * @param {string} entryUuid - The entry UUID to check
   * @returns {boolean} True if the entry is watched
   */
  isWatched(eventUuid, entryUuid) {
    // Access _version to create a tracked dependency
    // eslint-disable-next-line no-unused-vars
    const version = this._version;

    const watchedByEvent = this.storage.get('watchedEntriesByEvent') || {};
    const entries = watchedByEvent[eventUuid] || [];
    return entries.includes(entryUuid);
  }

  /**
   * Get all watched entry UUIDs for an event
   * @param {string} eventUuid - The event UUID
   * @returns {Array<string>} Array of entry UUIDs
   */
  getWatchedEntries(eventUuid) {
    // Access _version to create a tracked dependency
    // eslint-disable-next-line no-unused-vars
    const version = this._version;

    const watchedByEvent = this.storage.get('watchedEntriesByEvent') || {};
    return watchedByEvent[eventUuid] || [];
  }

  /**
   * Get the count of watched entries for an event
   * @param {string} eventUuid - The event UUID
   * @returns {number} Number of watched entries
   */
  getWatchedCount(eventUuid) {
    return this.getWatchedEntries(eventUuid).length;
  }

  /**
   * Clear all watched entries for an event
   * @param {string} eventUuid - The event UUID
   */
  clearWatchlist(eventUuid) {
    const watchedByEvent = this.storage.get('watchedEntriesByEvent') || {};
    watchedByEvent[eventUuid] = [];
    this.storage.set('watchedEntriesByEvent', { ...watchedByEvent });
    this._version++;
  }

  /**
   * Toggle an entry's watched status
   * @param {string} eventUuid - The event UUID
   * @param {string} entryUuid - The entry UUID to toggle
   * @returns {boolean} True if now watched, false if removed
   */
  toggleWatched(eventUuid, entryUuid) {
    if (this.isWatched(eventUuid, entryUuid)) {
      this.removeFromWatchlist(eventUuid, entryUuid);
      return false;
    } else {
      this.addToWatchlist(eventUuid, entryUuid);
      return true;
    }
  }
}
